import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { useColorScaleVDP, VerticalDotPlotAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { useConfig } from "../config";
import { metadata } from "../data/data_quality_fig_ELECTRIC";
import { datasets } from "../data/data_quality_fig_ELECTRIC_datasets";
import { countryIdIso, getCountryMeta, getLevelName, mkGemEntityDecoder, ordLevel, useButtonGroupState, useCountryEntities, useFigureControlItems, useFigureState, useNamedEntitiesWithLevel } from "../domain";
import { useMedia } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, identity, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_quality_fig_ELECTRIC";
const DEFAULT_LEVEL = "primary";
const Indicator = io.keyof({
    "SCHBSP.WINTERN": null,
    "SCHBSP.WELEC": null
});
const indicatorLookup = {
    "SCHBSP.WINTERN": defineMessage({
        id: "fig.quality.ELECTRIC.SCHBSP.WINTERN",
        defaultMessage: "Internet"
    }),
    "SCHBSP.WELEC": defineMessage({
        id: "fig.quality.ELECTRIC.SCHBSP.WELEC",
        defaultMessage: "Electricity"
    })
};
export const Data = mkGemEntityDecoder([
    "year",
    "level"
], {
    value: io.number,
    ind_id: Indicator
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const env = useConfig();
    const [selectedLevel] = useButtonGroupState("level");
    const { activeSection } = useReadScrolly();
    const entityData_ = useNamedEntitiesWithLevel(dataRaw);
    const entityData = React.useMemo(()=>{
        const data = entityData_.map((x)=>({
                ...x,
                ind_name: intl.formatMessage(indicatorLookup[x.ind_id])
            }));
        return Ar.sortBy([
            Ord.contramap((x)=>x.level)(ordLevel),
            Ord.contramap((x)=>x.ind_id === "SCHBSP.WELEC" ? 0 : 1)(Ord.ordNumber),
            Ord.contramap((x)=>x.value)(Ord.ordNumber)
        ])(data);
    }, [
        intl,
        entityData_
    ]);
    const countryData = useCountryEntities(entityData);
    const levelControlItems = useFigureControlItems(countryData, (x)=>x.level, (x)=>getLevelName(intl, x.level), {
        ord: ordLevel
    });
    const highlightItems = pipe(activeSection, O.map((x)=>x.split(",")), O.chain((strIds)=>Ar.array.sequence(O.option)(strIds.map((strId)=>getCountryMeta(env.countries, countryIdIso.wrap(strId))))), O.map((xs)=>xs.map((meta)=>meta.country)), O.chain((xs)=>An.fromArray(xs)));
    React.useEffect(()=>{
        actions.setSelectionControl("level", {
            type: "ButtonGroup",
            selected: DEFAULT_LEVEL
        }, levelControlItems);
    }, [
        actions,
        levelControlItems
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(countryData.filter((x)=>x.level === selectedLevel));
    }, [
        countryData,
        selectedLevel
    ]);
    const getX = React.useCallback((x)=>x.entity_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.ind_name, []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    const formatY = useFormat(".0%");
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <VerticalDotPlotAuto height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatY} domainY={[
        0,
        1
    ]} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip showLegend showYExtent={O.isNone(highlightItems)} dotStyle={"small"} labelStyleX="none" highlightItems={highlightItems}/>;
};
export default withFigureIO({
    url: new URL("../data/data_quality_fig_ELECTRIC.json", import.meta.url).href,
    csv: new URL("../data/data_quality_fig_ELECTRIC.zip", import.meta.url).href,
    xlsx: new URL("../data/data_quality_fig_ELECTRIC.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
